import * as React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { supabase } from "../logic/client";
import { useI18n } from "../i18n/I18nProvider";

const LAB_PHOTOS_BUCKET = "lab-photos";
const PAGE_SIZE = 100;
const MAX_FOLDER_DEPTH = 6;

type LabPhoto = {
  name: string;
  path: string;
  signedUrl: string;
  createdAt: string | null;
  updatedAt: string | null;
  size: number | null;
};

type StorageItem = {
  name: string;
  id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  metadata?: {
    mimetype?: string;
    size?: number;
  } | null;
};

type Props = {
  handlePageChange: (page: string) => void;
};

function isImageFile(item: StorageItem) {
  const mimetype = item.metadata?.mimetype ?? "";
  const name = item.name.toLowerCase();

  return (
    mimetype.startsWith("image/") ||
    /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(name)
  );
}

function joinPath(folder: string, name: string) {
  return folder ? `${folder}/${name}` : name;
}

function formatBytes(value: number | null) {
  if (!value) return "";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value: string | null, lang: string) {
  if (!value) return "";
  return new Date(value).toLocaleString(lang === "de" ? "de-DE" : "en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function LabPhotos(props: Props) {
  const { handlePageChange } = props;
  const { lang } = useI18n();
  const L = React.useCallback((en: string, de: string) => (lang === "de" ? de : en), [lang]);

  const [photos, setPhotos] = React.useState<LabPhoto[]>([]);
  const [selectedPhoto, setSelectedPhoto] = React.useState<LabPhoto | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const loadPhotos = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const collected: Omit<LabPhoto, "signedUrl">[] = [];

      const listFolder = async (folder: string, depth: number): Promise<void> => {
        if (depth > MAX_FOLDER_DEPTH) return;

        let offset = 0;

        while (true) {
          const { data, error: listError } = await supabase.storage
            .from(LAB_PHOTOS_BUCKET)
            .list(folder, {
              limit: PAGE_SIZE,
              offset,
              sortBy: { column: "created_at", order: "desc" },
            });

          if (listError) throw listError;
          if (!data?.length) break;

          for (const item of data as StorageItem[]) {
            const path = joinPath(folder, item.name);

            if (isImageFile(item)) {
              collected.push({
                name: item.name,
                path,
                createdAt: item.created_at ?? null,
                updatedAt: item.updated_at ?? null,
                size: item.metadata?.size ?? null,
              });
            } else {
              await listFolder(path, depth + 1);
            }
          }

          if (data.length < PAGE_SIZE) break;
          offset += PAGE_SIZE;
        }
      };

      await listFolder("", 0);

      const signedPhotos = await Promise.all(
        collected.map(async (photo) => {
          const { data, error: signedError } = await supabase.storage
            .from(LAB_PHOTOS_BUCKET)
            .createSignedUrl(photo.path, 60 * 60);

          if (signedError) throw signedError;

          return {
            ...photo,
            signedUrl: data.signedUrl,
          };
        })
      );

      signedPhotos.sort((a, b) => {
        const aTime = new Date(a.createdAt ?? a.updatedAt ?? 0).getTime();
        const bTime = new Date(b.createdAt ?? b.updatedAt ?? 0).getTime();
        return bTime - aTime;
      });

      setPhotos(signedPhotos);
    } catch (err: any) {
      console.error("load lab photos error", err);
      setError(err?.message ?? L("Could not load lab photos.", "Lab-Fotos konnten nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  }, [L]);

  React.useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  return (
    <Box sx={{ minHeight: "100vh", background: controlPanelBackground, color: "#fff", position: "relative" }}>
      <Box sx={{ position: "absolute", top: { xs: 12, md: 24 }, left: { xs: 12, md: 24 }, zIndex: 10 }}>
        <IconButton
          onClick={() => handlePageChange("ControlPanel")}
          sx={iconButtonSx}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      <Box sx={{ maxWidth: 1480, mx: "auto", p: { xs: 2, md: 4 }, pt: { xs: 8, md: 4 } }}>
        <Stack spacing={3}>
          <Paper sx={glassCard}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Stack spacing={1}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <ImageOutlinedIcon sx={{ color: "#ff3d4d" }} />
                  <Typography sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 950, lineHeight: 1.1 }}>
                    {L("Lab Photos", "Lab-Fotos")}
                  </Typography>
                </Stack>

                <Typography sx={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.55 }}>
                  {L(
                    "Photos uploaded by participants during the lab.",
                    "Fotos, die Teilnehmende während des Labs hochgeladen haben."
                  )}
                </Typography>
              </Stack>

              <Button
                startIcon={<RefreshIcon />}
                onClick={loadPhotos}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(255,255,255,0.06)",
                  "&:hover": { background: "rgba(255,255,255,0.12)" },
                }}
              >
                {L("Refresh", "Aktualisieren")}
              </Button>
            </Stack>
          </Paper>

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {loading && (
            <Box sx={{ minHeight: 360, display: "grid", placeItems: "center" }}>
              <Stack spacing={2} alignItems="center">
                <CircularProgress sx={{ color: "#ff3d4d" }} />
                <Typography sx={{ color: "rgba(255,255,255,0.72)", fontWeight: 800 }}>
                  {L("Loading lab photos...", "Lab-Fotos werden geladen...")}
                </Typography>
              </Stack>
            </Box>
          )}

          {!loading && photos.length === 0 && (
            <Paper sx={{ ...glassCard, textAlign: "center", py: { xs: 6, md: 8 } }}>
              <ImageOutlinedIcon sx={{ fontSize: 52, color: "rgba(255,255,255,0.35)", mb: 1 }} />
              <Typography sx={{ fontSize: 22, fontWeight: 900 }}>
                {L("No photos yet", "Noch keine Fotos")}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.62)", mt: 1 }}>
                {L("Uploaded lab photos will appear here.", "Hochgeladene Lab-Fotos erscheinen hier.")}
              </Typography>
            </Paper>
          )}

          {!loading && photos.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(3, 1fr)",
                  sm: "repeat(4, 1fr)",
                  md: "repeat(5, 1fr)",
                  lg: "repeat(6, 1fr)",
                },
                gap: { xs: 0.75, sm: 1, md: 1.25 },
              }}
            >
              {photos.map((photo) => (
                <Box
                  key={photo.path}
                  component="button"
                  type="button"
                  onClick={() => setSelectedPhoto(photo)}
                  sx={{
                    p: 0,
                    m: 0,
                    border: 0,
                    width: "100%",
                    aspectRatio: "1 / 1",
                    overflow: "hidden",
                    borderRadius: { xs: 1.25, sm: 1.75 },
                    cursor: "pointer",
                    background: "rgba(255,255,255,0.06)",
                    boxShadow: "0 12px 35px rgba(0,0,0,0.24)",
                    transition: "transform .18s ease, box-shadow .18s ease, filter .18s ease",
                    "&:hover": {
                      transform: "scale(1.025)",
                      boxShadow: "0 18px 48px rgba(0,0,0,0.42)",
                      filter: "brightness(1.08)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={photo.signedUrl}
                    alt={photo.name}
                    loading="lazy"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Stack>
      </Box>

      <Dialog
        open={Boolean(selectedPhoto)}
        onClose={() => setSelectedPhoto(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: "rgba(14,13,13,0.98)",
            color: "#fff",
            borderRadius: { xs: 0, sm: 3 },
            border: "1px solid rgba(255,255,255,0.12)",
          },
        }}
      >
        {selectedPhoto && (
          <DialogContent sx={{ p: 0, position: "relative" }}>
            <IconButton
              onClick={() => setSelectedPhoto(null)}
              sx={{
                ...iconButtonSx,
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 2,
              }}
            >
              <CloseIcon />
            </IconButton>

            <Box
              component="img"
              src={selectedPhoto.signedUrl}
              alt={selectedPhoto.name}
              sx={{
                width: "100%",
                maxHeight: "78vh",
                objectFit: "contain",
                display: "block",
                background: "#050505",
              }}
            />

            <Stack spacing={0.75} sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Typography sx={{ fontWeight: 900, wordBreak: "break-word" }}>
                {selectedPhoto.name}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 13, wordBreak: "break-word" }}>
                {selectedPhoto.path}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: 13 }}>
                {[formatDate(selectedPhoto.createdAt, lang), formatBytes(selectedPhoto.size)]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Stack>
          </DialogContent>
        )}
      </Dialog>
    </Box>
  );
}

const controlPanelBackground = `
  radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
  radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
  #111010
`;

const glassCard = {
  borderRadius: { xs: 3, sm: 5 },
  p: { xs: 2.5, sm: 4 },
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",
  backdropFilter: "blur(14px)",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "white",
};

const iconButtonSx = {
  color: "white",
  background: "rgba(255,255,255,0.06)",
  backdropFilter: "blur(6px)",
  border: "1px solid rgba(255,255,255,0.1)",
  boxShadow: "0 0 15px rgba(255,255,255,0.1)",
  "&:hover": { background: "rgba(255,255,255,0.12)" },
};
